import { useState, useEffect } from 'react';
import { Link } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/cn';
import { AppShell, PageHeader } from '../../../components/layout';
import { Button, Card, StepProgressBar } from '../../../components/ui';
import {
  ThinkingIndicator,
  FeedbackPanel,
  ScoreCircle,
  SkillTagList,
} from '../../../components/feedback';

export default function PracticeResults({
  student,
  session,
  responses,
  overall_score,
  has_practice_exercises,
}) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [revealedCount, setRevealedCount] = useState(0);

  // Simulate AI processing delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Reveal feedback panels one by one
  useEffect(() => {
    if (!isLoading && revealedCount < responses.length) {
      const timer = setTimeout(() => {
        setRevealedCount((prev) => prev + 1);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isLoading, revealedCount, responses.length]);

  const allRevealed = revealedCount >= responses.length;

  // Calculate skill summary
  const skillSummary = {};
  responses.forEach((response) => {
    if (response.skills) {
      response.skills.forEach((skill) => {
        if (!skillSummary[skill.name]) {
          skillSummary[skill.name] = { demonstrated: 0, total: 0 };
        }
        skillSummary[skill.name].total++;
        if (skill.status === 'demonstrated') {
          skillSummary[skill.name].demonstrated++;
        }
      });
    }
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950">
        <header className="sticky top-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-100 dark:border-gray-700">
          <div className="max-w-5xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {t('practice.step3Feedback')}
              </span>
            </div>
            <StepProgressBar
              currentStep={3}
              totalSteps={3}
              stepLabels={[t('practice.reading'), t('practice.questions'), t('practice.reviewLabel')]}
            />
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-16">
          <ThinkingIndicator message={t('practice.readingResponses')} />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-100 dark:border-gray-700">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {t('practice.step3Feedback')}
            </span>
            <Link href="/student/dashboard">
              <Button variant="ghost" size="sm">
                {t('practice.backToDashboard')}
              </Button>
            </Link>
          </div>
          <StepProgressBar
            currentStep={3}
            totalSteps={3}
            stepLabels={[t('practice.reading'), t('practice.questions'), t('practice.reviewLabel')]}
          />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Overall Score Card */}
        <Card gradient elevated className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            {t('practice.practiceComplete')}
          </h1>
          <div className="flex justify-center mb-6">
            <ScoreCircle score={overall_score} size="lg" />
          </div>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t('practice.greatEffort')}
          </p>

          {/* Skill Summary */}
          {Object.keys(skillSummary).length > 0 && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">
                {t('practice.skillsOverview')}
              </h2>
              <div className="flex flex-wrap justify-center gap-3">
                {Object.entries(skillSummary).map(([name, data]) => (
                  <div
                    key={name}
                    className={cn(
                      'px-3 py-2 rounded-lg',
                      data.demonstrated === data.total
                        ? 'bg-green-100 dark:bg-green-900/30'
                        : 'bg-amber-100 dark:bg-amber-900/30'
                    )}
                  >
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {name}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      {data.demonstrated}/{data.total} {t('practice.demonstrated')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Feedback Panels */}
        <div className="space-y-6 mb-8">
          {responses.map((response, index) => (
            <div
              key={response.id}
              className={cn(
                'transition-all duration-500 ease-out',
                index < revealedCount
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-4'
              )}
            >
              <FeedbackPanel
                questionNumber={index + 1}
                questionPrompt={response.question_prompt}
                questionType={response.question_type}
                studentResponse={response.content}
                score={response.score}
                feedback={response.feedback}
                thinking={response.thinking}
                skills={response.skills}
              />
            </div>
          ))}
        </div>

        {/* Continue Button */}
        {allRevealed && (
          <div
            className={cn(
              'text-center transition-all duration-500',
              allRevealed ? 'opacity-100' : 'opacity-0'
            )}
          >
            {has_practice_exercises ? (
              <Link href={`/student/practice/${session.id}/exercises`}>
                <Button variant="primary" size="lg">
                  {t('practice.continueToPractice')}
                  <svg
                    className="ml-2 w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Button>
              </Link>
            ) : (
              <Link href="/student/dashboard">
                <Button variant="primary" size="lg">
                  {t('practice.backToDashboard')}
                </Button>
              </Link>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
